import {
  Alert,
  Box,
  Breadcrumbs,
  Chip,
  CircularProgress,
  Container,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { RecordSection } from '../components/record/RecordPage.tsx';
import { formatReportDate, getErrorMessage } from '../utils/helperUtils.ts';
import { fetchTenants, type TenantSummary } from './platformApi.ts';
import TenantStatusChip from './TenantStatusChip.tsx';

const peopleText = ({ users }: TenantSummary) =>
  [
    `${users.active} active`,
    users.invited ? `${users.invited} invited` : null,
    users.revoked ? `${users.revoked} revoked` : null
  ]
    .filter(Boolean)
    .join(' · ');

/** Platform: every workspace on Forge, for the addresses in PLATFORM_ADMIN_EMAILS. */
const PlatformTenantsPage = () => {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<TenantSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTenants()
      .then(setTenants)
      .catch((err: unknown) => setError(getErrorMessage(err)));
  }, []);

  return (
    <Container maxWidth="lg">
      <Breadcrumbs sx={{ mt: 2 }}>
        <MuiLink component={RouterLink} underline="hover" color="inherit" to="/">
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Platform</Typography>
      </Breadcrumbs>

      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography
          variant="overline"
          sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.12em' }}
        >
          NLC
        </Typography>
        <Typography variant="h4" sx={{ mt: 0.25, fontWeight: 750, letterSpacing: '-0.025em' }}>
          Platform
        </Typography>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load the workspaces: {error}
        </Alert>
      ) : null}

      {tenants ? (
        <RecordSection
          title={`Workspaces (${tenants.length})`}
          description="Every tenant on Forge. Open one to manage its people or step in to help."
        >
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Subscription</TableCell>
                  <TableCell>People</TableCell>
                  <TableCell>Last active</TableCell>
                  <TableCell>Started</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map((tenant) => (
                  <TableRow
                    key={tenant.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/platform/tenants/${tenant.id}`)}
                  >
                    <TableCell>
                      <MuiLink
                        component={RouterLink}
                        to={`/platform/tenants/${tenant.id}`}
                        onClick={(event) => event.stopPropagation()}
                        sx={{ fontWeight: 600 }}
                      >
                        {tenant.name}
                      </MuiLink>
                    </TableCell>
                    <TableCell>
                      <TenantStatusChip tenant={tenant} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {peopleText(tenant)}
                      {!tenant.demo && tenant.users.admins === 0 ? (
                        <Chip
                          size="small"
                          label="No admin"
                          color="warning"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>{formatReportDate(tenant.lastSeenAt) || '—'}</TableCell>
                    <TableCell>{formatReportDate(tenant.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </RecordSection>
      ) : error ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading workspaces
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default PlatformTenantsPage;
